import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-buttons';
import 'datatables.net-buttons-dt/css/buttons.dataTables.css';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from 'react-router-dom';
import { baseurl } from '../utility/helper';
import { StatusBadge, formatDateTime } from '../utility/instituteHelpers';
import ViewCertificate from './ViewCertificate';
import { generateCertificate } from './generateCertificate';
import { downloadHallTicket } from './hallTicket';
import '../assets/css/Institute.css';

// Institute tests are rows in public.test (institute_test = 1) with candidates in
// test_registration, so the candidate grid below reuses the same
// /admin/testcandidates/* endpoints as EditTest: attendance, Excel result upload,
// hall ticket, pass/fail letter, certificate upload/view, result + reminder mail.
// The column layout matches EditTest so one Excel template works for both.

const BatchTestDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instituteTestId, batchId } = location.state || {};
  const testId = instituteTestId;

  const roleid = parseInt(sessionStorage.getItem('userroleId'));
  const initialChapter = roleid === 2 ? sessionStorage.getItem('userchapterId') || '' : '';

  const tableRef = useRef(null);
  // When true, the next run of the table-build effect is skipped so an
  // attendance tick updates the grid in place instead of rebuilding it.
  const skipRebuildRef = useRef(false);

  const [test, setTest] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [proctorName, setProctorName] = useState('');

  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [modalUserCert, setModalUserCert] = useState([]);
  const [currentCertIndex, setCurrentCertIndex] = useState(0);

  const canManage = !!test && (roleid === 1 || String(initialChapter) === String(test.chapter_id));
  const canUploadCert = canManage || roleid === 4;

  const fetchCandidates = useCallback(async () => {
    if (!testId) return;
    try {
      const response = await axios.get(`${baseurl}/admin/testcandidates?testId=${testId}`);
      setCandidates(response.data);
    } catch (err) {
      console.error('Error fetching candidate data:', err);
    }
  }, [testId]);

  useEffect(() => {
    if (!testId) return;
    axios.get(`${baseurl}/admin/institute-tests/${testId}`)
      .then((r) => {
        setTest(r.data);
        setProctorName(r.data.proctor_name || '');
      })
      .catch((err) => console.error('Error loading test detail:', err));
    fetchCandidates();
  }, [testId, fetchCandidates]);

  // ── Helpers: track selected IDs ──────────────────────────────────────────
  const updateSelectedCandidates = () => {
    const selected = [];
    $(tableRef.current).find('.row-checkbox:checked').each(function () { selected.push($(this).data('id')); });
    setSelectedCandidates(selected);
  };

  const updateSelectedReminders = () => {
    const selected = [];
    $(tableRef.current).find('.reminder-checkbox:checked').each(function () { selected.push($(this).data('id')); });
    setSelectedReminders(selected);
  };

  const openCertificates = async (candidateId) => {
    const candidate = candidates.find((x) => x.test_candidate_id === candidateId);
    if (candidate?.files?.length > 0) {
      setModalUserCert(candidate.files); setShowModal(true); setCurrentCertIndex(0);
      return;
    }
    try {
      const url = `${baseurl}/admin/testcandidates/certificate/download?testId=${testId}&candidateId=${candidateId}`;
      const response = await axios.get(url);
      if (response.data?.url?.length > 0) {
        if (candidate) candidate.files = response.data.url;
        setModalUserCert(response.data.url); setShowModal(true); setCurrentCertIndex(0);
      } else { alert('No certificates found.'); }
    } catch { alert('Failed to load certificates.'); }
  };

  useEffect(() => {
    const tableElement = tableRef.current;
    if (!tableElement || !test) return;

    if (skipRebuildRef.current) {
      skipRebuildRef.current = false;
      return;
    }

    if ($.fn.DataTable.isDataTable(tableElement)) {
      $(tableElement).DataTable().destroy();
    }
    $(tableElement).empty();

    // Hall ticket / letters read the legacy test field names.
    const testInfo = { ...test, test_title: test.test_name };
    const testDate = test.test_start_time ? dayjs(test.test_start_time).format('DD MMM YYYY, hh:mm A') : '—';
    const venue = test.venue || '—';
    const proctor = test.proctor_name || '—';
    const disabledBox = (label) =>
      `<div style="text-align:center;color:gray;padding:4px 8px;border:1px solid #ccc;border-radius:4px;background:#f0f0f0;cursor:not-allowed;font-size:12px">${label}</div>`;

    $(tableElement).DataTable({
      dom: 'Blfrtip',
      pageLength: 25,
      lengthMenu: [[10, 25, 50, 100, 250, 500, -1], [10, 25, 50, 100, 250, 500, 'All']],
      buttons: [
        { extend: 'copyHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible:not(.notexport)' } },
        {
          extend: 'excelHtml5',
          className: 'btn btn-primary dttable',
          filename: () => 'Mensa_Test_' + (test.test_name || 'Default'),
          title: null,
          exportOptions: {
            columns: ':visible:not(.notexport)',
            rows: (idx, data) => data.attendance === true, // Only Present
            format: {
              body: function (data, row, column, node) {
                if ([20, 21, 22, 23, 24].includes(column)) return '';
                if (column === 4) {
                  const checkbox = $(node).find('input[type="checkbox"]');
                  if (checkbox.length) return checkbox.is(':checked') ? 'Present' : 'Absent';
                  const text = $(node).text().trim();
                  if (text.includes('Present')) return 'Present';
                  if (text.includes('Absent')) return 'Absent';
                  return '';
                }
                return $('<div>').html(data).text();
              },
            },
          },
          customize: function (xlsx) {
            const sheet = xlsx.xl.worksheets['sheet1.xml'];
            const $sheet = $(sheet);
            const attendeeCount = candidates.filter((c) => c.attendance === true).length;
            const summaryRow = `
            <row r="1">
              <c r="A1" t="inlineStr"><is><t>Date of Test: ${testDate}</t></is></c>
              <c r="D1" t="inlineStr"><is><t>Location: ${venue}</t></is></c>
              <c r="G1" t="inlineStr"><is><t>Name of Proctor: — ${proctor}</t></is></c>
              <c r="J1" t="inlineStr"><is><t>No. of Attendees: ${attendeeCount} / ${candidates.length}</t></is></c>
            </row>`;

            $sheet.find('row').each(function () {
              const r = parseInt($(this).attr('r'));
              $(this).attr('r', r + 1);
              $(this).find('c').each(function () {
                const ref = $(this).attr('r');
                if (ref) $(this).attr('r', ref.replace(/\d+$/, (m) => String(parseInt(m) + 1)));
              });
            });
            $sheet.find('sheetData').prepend(summaryRow);

            const mergeCells = `
            <mergeCells count="4">
              <mergeCell ref="A1:C1"/>
              <mergeCell ref="D1:F1"/>
              <mergeCell ref="G1:I1"/>
              <mergeCell ref="J1:L1"/>
            </mergeCells>`;
            $sheet.find('mergeCells').remove();
            $sheet.find('sheetData').after(mergeCells);
          },
        },
        { extend: 'csvHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible:not(.notexport)' } },
        { extend: 'pdfHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible:not(.notexport)' } },
        { extend: 'print', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible:not(.notexport)' } },
      ],
      data: candidates,
      order: [[0, 'asc']],
      initComplete: function () {
        $('.dt-length select').addClass('form-select form-select-sm');
        const attendeeCount = candidates.filter((c) => c.attendance === true).length;
        const thead = $(tableElement).find('thead');
        if (thead.find('.summary-row').length) return;
        const colCount = thead.find('tr:first th').length;
        thead.prepend(`
          <tr class="summary-row" style="background:#f0f4ff;border-top:2px solid #dee2e6;">
            <th colspan="${colCount}" style="padding:10px 14px;font-size:13px;font-weight:normal;border-bottom:2px solid #adb5bd;white-space:nowrap;">
              <span style="margin-right:48px;"><strong>Date of Test:</strong> ${testDate}</span>
              <span style="margin-right:48px;"><strong>Location:</strong> ${venue}</span>
              <span style="margin-right:48px;"><strong>Name of Proctor:</strong> — ${proctor}</span>
              <span><strong>No. of Attendees:</strong> <span class="attendee-count">${attendeeCount} / ${candidates.length}</span></span>
            </th>
          </tr>`);
      },
      columns: [
        { data: null, title: 'Sr. No', render: (data, type, row, meta) => meta.row + 1 },
        {
          data: 'test_candidate_id',
          title: 'ID',
          render: (data, type, row) => {
            const candidateId = row.test_candidate_id;
            if (type !== 'display') return candidateId || '';
            if (!candidateId) return '<span class="cl-muted">—</span>';
            return `<a href="/admin/CandiAdd/${candidateId}" class="cl-id-link"
                      onclick="event.preventDefault(); window.open(this.href, '_blank');">${candidateId}</a>`;
          },
        },
        {
          data: 'datetime',
          title: 'Register Date',
          render: function (data, type) {
            if (!data) return '';
            const d = new Date(data);
            if (type === 'sort') return d.getTime();
            return d.toLocaleString('en-IN', {
              timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true,
            }).replace(',', '');
          },
        },
        { data: null, title: 'Name', render: (data, type, row) => `${row.first_name || ''} ${row.last_name || ''}` },
        {
          title: 'Attendance', data: 'attendance',
          render: function (data, type, row) {
            const checked = data === true ? 'checked' : '';
            if (canManage) {
              return `<center><input type="checkbox" class="attendance-checkbox" data-id="${row.test_candidate_id}" ${checked}/></center>`;
            }
            if (data === true) return '<span class="badge bg-success">Present</span>';
            if (data === false) return '<span class="badge bg-danger">Absent</span>';
            return '';
          },
        },
        {
          data: 'profile_image',
          title: 'Photo',
          orderable: false,
          className: 'notexport',
          render: function (data) {
            if (!data) {
              return '<div style="width:40px;height:40px;border-radius:50%;background:var(--bs-secondary-bg,#e9ecef);display:flex;align-items:center;justify-content:center;font-size:18px;margin:auto;">👤</div>';
            }
            return `<img src="${data}" alt="Photo"
              style="width:40px;height:40px;border-radius:50%;object-fit:cover;display:block;margin:auto;border:1px solid #dee2e6;"
              onerror="this.style.display='none'" />`;
          },
        },
        { data: 'mobile_no', title: 'Mobile No', className: 'text-left' },
        { data: 'email', title: 'Email' },
        { data: 'address', title: 'Address' },
        { data: 'gender', title: 'Gender' },
        {
          data: 'dob', title: 'DOB',
          render: function (data) {
            if (!data) return '';
            const d = new Date(data);
            return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
          },
        },
        { data: 'state', title: 'State' },
        { data: 'city', title: 'City' },
        { data: 'last_education', title: 'Last Education' },
        { data: 'met1_pr', title: 'MET1', render: (data) => data ?? '' },
        { data: 'met1_gr', title: 'MET1- GR', render: (data) => data ?? '' },
        { data: 'met2_pr', title: 'MET2', render: (data) => data ?? '' },
        { data: 'met2_gr', title: 'MET2- GR', render: (data) => data ?? '' },
        {
          data: 'marks_obtain',
          title: 'Marks Obtained',
          render: (data) => {
            if (data == null) return '';
            return data < 25 ? 'Below 25' : data;
          },
        },
        { data: 'result_status', title: 'Result', render: (data) => data ?? '' },
        {
          title: 'Hall Ticket',
          data: null,
          orderable: false,
          className: 'notexport',
          render: (data, type, row) =>
            `<button class="btn btn-info btn-sm hall-ticket-btn" data-id="${row.test_candidate_id}">🎫 Hall Ticket</button>`,
        },
        {
          title: 'Generate Cert.', data: null, orderable: false,
          render: function (data, type, row) {
            const hasResult = row.result_status !== null && row.result_status !== undefined && row.result_status !== '';
            if (!hasResult) return disabledBox('No Result');
            const isPass = String(row.result_status).toLowerCase() === 'pass';
            return isPass
              ? `<button class="btn btn-success btn-sm gen-cert-btn" data-id="${row.test_candidate_id}" title="Download Pass Letter">✅ Pass Letter</button>`
              : `<button class="btn btn-warning btn-sm gen-cert-btn" data-id="${row.test_candidate_id}" title="Download Fail Letter" style="color:#333">📄 Fail Letter</button>`;
          },
        },
        {
          title: 'Upload Cert.',
          render: function (data, type, row) {
            if (canUploadCert && row.result_status != null) {
              return `<button class='btn btn-primary btn-sm upload-btn' data-id='${row.test_candidate_id}'>Upload</button>`;
            }
            return disabledBox('Disabled');
          },
        },
        {
          title: 'View Cert.',
          render: (data, type, row) => (row.certificate != null
            ? `<button class="btn btn-success btn-sm view-btn" data-id="${row.test_candidate_id}">View</button>`
            : disabledBox('Disabled')),
        },
        {
          title: "<input type='checkbox' id='select-all' class='styled-checkbox'> Send Email",
          data: null, orderable: false,
          render: function (data, type, row) {
            if (!canManage) return '';
            const isAbsent = row.attendance === false || row.attendance === null || row.attendance === undefined;
            const disabled = isAbsent ? 'disabled' : '';
            const disabledStyle = isAbsent ? 'opacity:0.4; cursor:not-allowed;' : '';
            return `<center><input type='checkbox' class='row-checkbox styled-checkbox' data-id='${row.test_candidate_id}' ${disabled} style='${disabledStyle}'/></center>`;
          },
        },
        {
          title: "<input type='checkbox' id='select-all-reminder' class='styled-checkbox'> Send Reminder",
          data: null, orderable: false,
          render: (data, type, row) => (canManage
            ? `<center><input type='checkbox' class='reminder-checkbox styled-checkbox' data-id='${row.test_candidate_id}'/></center>`
            : ''),
        },
      ],
    });

    const $table = $(tableElement);

    // ── Select All (Send Email / Send Reminder) ──────────────────────────────
    $table.off('click', '#select-all').on('click', '#select-all', function () {
      $table.find('.row-checkbox:not(:disabled)').prop('checked', $(this).prop('checked'));
      updateSelectedCandidates();
    });
    $table.off('click', '#select-all-reminder').on('click', '#select-all-reminder', function () {
      $table.find('.reminder-checkbox').prop('checked', $(this).prop('checked'));
      updateSelectedReminders();
    });

    // ── Hall Ticket ──────────────────────────────────────────────────────────
    $table.off('click', '.hall-ticket-btn').on('click', '.hall-ticket-btn', function () {
      const candidate = candidates.find((c) => c.test_candidate_id === $(this).data('id'));
      if (candidate) downloadHallTicket(testInfo, candidate);
    });

    // ── Attendance ───────────────────────────────────────────────────────────
    $table.off('change', '.attendance-checkbox').on('change', '.attendance-checkbox', async function () {
      const candidateId = $(this).data('id');
      const isPresent = $(this).is(':checked');
      try {
        await axios.patch(
          `${baseurl}/admin/testcandidates/attendance?testId=${testId}&candidateId=${candidateId}`,
          { attendance: isPresent },
          { headers: { Authorization: sessionStorage.getItem('token'), 'Content-Type': 'application/json' } }
        );

        // Update the grid in place — a full rebuild would scroll to the top.
        const dt = $table.DataTable();
        const rowIdx = dt.rows().indexes().toArray()
          .find((i) => dt.row(i).data() && dt.row(i).data().test_candidate_id === candidateId);
        if (rowIdx !== undefined) {
          const d = dt.row(rowIdx).data();
          d.attendance = isPresent;
          dt.row(rowIdx).data(d);
        }
        dt.draw(false);

        const rows = dt.rows().data().toArray();
        const attendeeCount = rows.filter((c) => c.attendance === true).length;
        $table.find('.attendee-count').text(`${attendeeCount} / ${rows.length}`);

        skipRebuildRef.current = true;
        setCandidates((prev) =>
          prev.map((c) => (c.test_candidate_id === candidateId ? { ...c, attendance: isPresent } : c))
        );
      } catch (err) {
        console.error('Failed to update attendance:', err);
        $(this).prop('checked', !isPresent);
      }
    });

    // ── Generate Pass / Fail Letter ──────────────────────────────────────────
    $table.off('click', '.gen-cert-btn').on('click', '.gen-cert-btn', async function () {
      const candidate = candidates.find((c) => c.test_candidate_id === $(this).data('id'));
      if (!candidate) return;
      const $btn = $(this);
      $btn.prop('disabled', true).text('Generating…');
      try {
        await generateCertificate({
          candidate,
          testDate: test.test_start_time ? dayjs(test.test_start_time).format('D MMMM YYYY') : '—',
          testLocation: test.venue || '—',
          format: 'pdf',
        });
      } catch (err) {
        console.error('Certificate error:', err);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Could not generate certificate. Please try again.' });
      } finally {
        const isPass = String(candidate.result_status).toLowerCase() === 'pass';
        $btn.prop('disabled', false).text(isPass ? '✅ Pass Letter' : '📄 Fail Letter');
      }
    });

    // ── Upload Certificate ───────────────────────────────────────────────────
    $table.off('click', '.upload-btn').on('click', '.upload-btn', function () {
      const candidateId = $(this).data('id');
      const input = $('<input>').attr({ type: 'file', multiple: true });
      input.on('change', function () {
        const files = this.files;
        if (!files.length) return;
        const fd = new FormData();
        for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
        axios.post(
          `${baseurl}/admin/testcandidates/certificate/upload?testId=${testId}&candidateId=${candidateId}`,
          fd, { headers: { 'Content-Type': 'multipart/form-data' } }
        )
          .then(async () => { await fetchCandidates(); alert('Upload successful'); })
          .catch(() => alert('Upload failed'));
      });
      input.trigger('click');
    });

    // ── View Certificate ─────────────────────────────────────────────────────
    $table.off('click', '.view-btn').on('click', '.view-btn', function () {
      openCertificates($(this).data('id'));
    });

    // ── Row checkboxes (Send Email / Send Reminder) ──────────────────────────
    $table.off('click', '.row-checkbox').on('click', '.row-checkbox', updateSelectedCandidates);
    $table.off('click', '.reminder-checkbox').on('click', '.reminder-checkbox', updateSelectedReminders);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, test]);

  // Tear the DataTable down on unmount.
  useEffect(() => () => {
    const el = tableRef.current;
    if (el && $.fn.DataTable.isDataTable(el)) $(el).DataTable().destroy();
  }, []);

  if (!testId) {
    return (
      <div className="content container-fluid inst-wrap">
        <div className="inst-card">No test selected. <button className="btn btn-link" onClick={() => navigate('/admin/BatchList')}>Go to Batches</button></div>
      </div>
    );
  }

  // ── Send Result Email ─────────────────────────────────────────────────────
  const handleSendMail = async () => {
    if (!selectedCandidates.length) { alert('Please select at least one candidate.'); return; }
    try {
      await axios.post(`${baseurl}/admin/testcandidates/result/send_email?testId=${testId}`, { ids: selectedCandidates });
      alert('Emails sent successfully!');
    } catch { alert('Error sending emails. Check console.'); }
  };

  // ── Send Reminder Email ───────────────────────────────────────────────────
  const handleSendReminder = async () => {
    if (!selectedReminders.length) { alert('Please select at least one candidate.'); return; }
    const result = await Swal.fire({
      title: 'Send Reminder?',
      text: `Send a reminder email to ${selectedReminders.length} selected candidate(s)?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send Reminder',
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(
        `${baseurl}/admin/testcandidates/reminder/send_email?testId=${testId}`,
        { ids: selectedReminders },
        { headers: { Authorization: sessionStorage.getItem('token'), 'Content-Type': 'application/json' } }
      );
      Swal.fire({ icon: 'success', title: 'Sent!', text: 'Reminder emails sent successfully.', timer: 2000, showConfirmButton: false });
      $(tableRef.current).find('.reminder-checkbox, #select-all-reminder').prop('checked', false);
      setSelectedReminders([]);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to send reminder emails.' });
    }
  };

  // ── Upload Result (Excel exported from this grid) ─────────────────────────
  const handleResultSubmit = async () => {
    if (!file) { setError('Please select an Excel file.'); return; }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
      // Row 1 = summary line, row 2 = header.
      const dataRows = rows.slice(2).filter((row) => row[1] !== null && row[1] !== undefined);
      const getNumber = (val) => (val ? Number(String(val).replace('+', '')) || 0 : 0);
      const normalizedCandidates = dataRows.map((row) => {
        const met1_pr = row[13] ? String(row[13]).trim() : null;
        const met2_pr = row[15] ? String(row[15]).trim() : null;
        let marks_obtain = null;
        if (met1_pr === '99+' || met2_pr === '99+') {
          marks_obtain = '99+';
        } else if (met1_pr !== null || met2_pr !== null) {
          marks_obtain = `${Math.max(getNumber(met1_pr), getNumber(met2_pr))}`;
        }
        return {
          test_candidate_id: row[1],
          met1_pr,
          met1_gr: row[14] ?? null,
          met2_pr,
          met2_gr: row[16] ?? null,
          marks_obtain,
          result: row[18] ? row[18].charAt(0).toUpperCase() + row[18].slice(1).toLowerCase() : null,
        };
      });
      try {
        await axios.post(`${baseurl}/admin/testcandidates/result?testId=${testId}`, { candidates: normalizedCandidates });
        await fetchCandidates();
        alert('Upload Result successfully');
        handleModalClose();
      } catch (err) {
        console.error('Failed to submit results:', err);
        setError('Failed to upload results.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validateFile = (f) => {
    const valid = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (f && valid.includes(f.type)) { setError(''); return true; }
    setError('Please upload a valid Excel file (.xls, .xlsx).');
    return false;
  };
  const simulateUploadProgress = () => {
    const interval = setInterval(() => {
      setUploadProgress((prev) => { if (prev >= 100) { clearInterval(interval); return 100; } return prev + 10; });
    }, 300);
  };
  const handleFileUpload = (e) => { const f = e.target.files[0]; if (validateFile(f)) { setFile(f); simulateUploadProgress(); } };
  const handleDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (validateFile(f)) { setFile(f); simulateUploadProgress(); } };
  const handleModalClose = () => { setFile(null); setUploadProgress(0); setError(''); setShow(false); };

  const handleProctorSave = async () => {
    try {
      await axios.patch(
        `${baseurl}/admin/tests/${testId}/proctor`,
        { proctor_name: proctorName },
        { headers: { Authorization: sessionStorage.getItem('token'), 'Content-Type': 'application/json' } }
      );
      // Rebuilds the grid so the summary row picks up the new name.
      setTest((prev) => ({ ...prev, proctor_name: proctorName }));
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Proctor name saved.', timer: 1500, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save proctor name.' });
    }
  };

  return (
    <div className="content container-fluid inst-wrap">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h2 className="mb-0"><b>{test?.test_name || 'Test'}</b></h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary btn-sm"
            onClick={() => navigate('/admin/BatchTestForm', { state: { batchId: batchId || test?.batch_id } })}>
            <i className="bx bxs-plus-circle" /> Add Test
          </button>
          {test && (
            <button className="btn btn-warning btn-sm"
              onClick={() => navigate('/admin/BatchTestForm', { state: { instituteTestId, batchId: batchId || test.batch_id } })}>
              Edit Test
            </button>
          )}
          {(canManage || roleid === 4) && (
            <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>
              <i className="bx bxs-plus-circle mr-2" /> Upload Result
            </button>
          )}
          <button className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate('/admin/BatchDashboard', { state: { batchId: batchId || test?.batch_id } })}>
            ← Back to Batch
          </button>
        </div>
      </div>

      {test && (
        <div className="inst-card">
          <div className="inst-overview-grid">
            <div className="inst-overview-tile"><div className="label">Code</div><div className="value">{test.test_code}</div></div>
            <div className="inst-overview-tile"><div className="label">Institute</div><div className="value" style={{ fontSize: '0.95rem' }}>{test.institute_name}</div></div>
            <div className="inst-overview-tile"><div className="label">Batch</div><div className="value" style={{ fontSize: '0.95rem' }}>{test.batch_name} ({test.batch_code})</div></div>
            <div className="inst-overview-tile"><div className="label">Venue</div><div className="value" style={{ fontSize: '0.95rem' }}>{test.venue || '—'}</div></div>
            <div className="inst-overview-tile"><div className="label">Start</div><div className="value" style={{ fontSize: '0.9rem' }}>{formatDateTime(test.test_start_time) || '—'}</div></div>
            <div className="inst-overview-tile"><div className="label">End</div><div className="value" style={{ fontSize: '0.9rem' }}>{formatDateTime(test.test_end_time) || '—'}</div></div>
            <div className="inst-overview-tile"><div className="label">Status</div><div className="value"><StatusBadge status={test.status} /></div></div>
          </div>
        </div>
      )}

      <ViewCertificate isOpen={showModal} onClose={() => setShowModal(false)}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh', width: '100%' }}>
          <div style={{ width: '80%', height: '90%', backgroundColor: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 0 10px rgba(0,0,0,0.2)', overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h2 style={{ textAlign: 'center' }}>Uploaded Certificates</h2>
            {modalUserCert.length > 0 ? (() => {
              const currentFile = modalUserCert[currentCertIndex];
              if (!currentFile) return <p>No file available</p>;
              const fileName = currentFile.filename || '';
              const isPDF = fileName.toLowerCase().includes('.pdf');
              return (
                <div style={{ textAlign: 'center', flexGrow: 1 }}>
                  <div style={{ position: 'relative', display: 'inline-block', maxWidth: '80vw' }}>
                    {isPDF ? (
                      <iframe
                        src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(currentFile.presignedUrl)}`}
                        title="PDF Viewer"
                        style={{ width: '100%', height: '60vh', border: '1px solid #ccc', borderRadius: '8px' }}
                      />
                    ) : (
                      <img
                        src={currentFile.presignedUrl}
                        alt="Certificate"
                        style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: '8px', border: '1px solid #ccc' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {modalUserCert.length > 1 && (
                      <>
                        <button onClick={() => setCurrentCertIndex((p) => (p === 0 ? modalUserCert.length - 1 : p - 1))}
                          style={{ position: 'absolute', left: '-40px', top: '50%', transform: 'translateY(-50%)' }}>&#8592;</button>
                        <button onClick={() => setCurrentCertIndex((p) => (p + 1) % modalUserCert.length)}
                          style={{ position: 'absolute', right: '-40px', top: '50%', transform: 'translateY(-50%)' }}>&#8594;</button>
                      </>
                    )}
                    <p style={{ marginTop: '10px' }}>{fileName}</p>
                    <a
                      href={currentFile.presignedUrl}
                      download={currentFile.filename}
                      target="_blank" rel="noopener noreferrer"
                      style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}
                    >
                      ⬇️ Download Certificate
                    </a>
                  </div>
                </div>
              );
            })() : <p>No certificates available</p>}
          </div>
        </div>
      </ViewCertificate>

      {/* Upload Result Modal */}
      <div className={`modal ${show ? 'show' : ''}`} style={{ display: show ? 'block' : 'none' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Upload Result</h5>
              <button type="button" className="btn-close" onClick={handleModalClose}></button>
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-2">
                Use the Excel downloaded from the grid below — fill MET1 / MET1-GR / MET2 / MET2-GR / Result and upload it back.
              </p>
              <input type="file" accept=".xls,.xlsx" onChange={handleFileUpload} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} />
              {error && <p className="text-danger">{error}</p>}
              {file && <p>Selected file: {file.name}</p>}
              <div className="progress">
                <div className="progress-bar" role="progressbar" style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100">
                  {uploadProgress}%
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleModalClose}>Close</button>
              <button type="button" className="btn btn-primary" onClick={handleResultSubmit}>Submit</button>
            </div>
          </div>
        </div>
      </div>

      <div className="inst-card">
        <h4 className="mb-3">Candidates Result ({candidates.length})</h4>

        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          {selectedCandidates.length > 0 && (
            <button className="btn btn-warning" onClick={handleSendMail}>
              ✉ Send Test Mail to {selectedCandidates.length} Candidate{selectedCandidates.length > 1 ? 's' : ''}
            </button>
          )}
          {selectedReminders.length > 0 && (
            <button className="btn btn-info text-white" onClick={handleSendReminder}>
              🔔 Send Reminder to {selectedReminders.length} Candidate{selectedReminders.length > 1 ? 's' : ''}
            </button>
          )}
        </div>

        {canManage && (
          <div className="d-flex align-items-center gap-2 mb-3" style={{ maxWidth: '420px' }}>
            <label className="fw-semibold mb-0 text-nowrap">Name of Proctor:</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Enter proctor name"
              value={proctorName}
              onChange={(e) => setProctorName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleProctorSave(); }}
            />
            <button className="btn btn-primary btn-sm text-nowrap" onClick={handleProctorSave}>Enter</button>
          </div>
        )}

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table ref={tableRef} className="table table-striped table-bordered" style={{ width: '100%' }}></table>
        </div>
      </div>
    </div>
  );
};

export default BatchTestDetail;
